import { createContext, useContext, useEffect, useReducer } from "react";
import { ReactChildren } from "../../interfaces/Common";
import { getAffiliations } from "../../Utils/apiService";
import KenLoader from "../../GlobalComponents/KenLoader";
import { useAuthContext } from "./authContext";
import { AuthState } from "../../interfaces/Contexts";
import { enqueueSnackbar } from "notistack";

const initialCourseState = {
  current: {},
  allCourses: [],
  trigger: 0,
};

const CourseContext = createContext<any>(null);

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case "SET_COURSE":
      return action.payload;
    case "TRIGGER":
      return { ...state, trigger: state.trigger + 1 };
    case "SWITCH_COURSE":
      const newCurrent = state.allCourses.find((course: any) => {
        return course.Id !== state.current.Id;
      });
      return { 
        ...state, 
        current: newCurrent,
        allCourses: state.allCourses.map((course: any) => {
          if (course.Id === newCurrent.Id) {
            return { ...course, isCurrent: true };
          }
          if (course.Id === state.current.Id) {
            return { ...course, isCurrent: false };
          }
          return course;
        })
      };
    case "RESET":
      return initialCourseState;
    default:
      return state;
  }
};

export const CourseContentProvider: React.FunctionComponent<ReactChildren> = (props) => {
  const [courseContent, dispatchCourse] = useReducer(reducer, initialCourseState);
  const { state: userData, dispatch } = useAuthContext();

  useEffect(() => {
    const { user }: AuthState = userData;
    const hasUser = !!user && !!user?.applicantId;
    const hasCurrentCourse = Object.keys(courseContent?.current || {}).length > 0;
    
    if (hasUser && !hasCurrentCourse) {
      getAffiliations(user?.applicantId as string)
        .then((res) => {
          if (!res || (typeof res === 'object' && res.isAxiosError)) {
            console.error('Invalid response from getAffiliations:', res);
            enqueueSnackbar("Failed to fetch course information. Please try again.", { variant: "error" });
            return;
          }
          
          const courses = Array.isArray(res) ? res : (res?.data || res?.result || []);
          
          if (!Array.isArray(courses) || courses.length === 0) {
            enqueueSnackbar("No courses found", { variant: "error" });
            dispatch({
              type: "LOGOUT",
            });
            return;
          }
          
          dispatchCourse({
            type: "SET_COURSE",
            payload: { allCourses: courses, current: courses[0] },
          });
        })
        .catch((err) => {
          console.error('Error fetching affiliations:', err);
          const errorMessage = err?.response?.data?.errorMessage || err?.message || "Failed to fetch course information. Please try again.";
          enqueueSnackbar(errorMessage, { variant: "error" });
        });
    }
  }, [userData?.user?.applicantId, courseContent?.trigger]);

  return <CourseContext.Provider value={{ value: courseContent, dispatch: dispatchCourse }}>{userData.user?.applicantId && Object.keys(courseContent?.current || {}).length === 0 ? <KenLoader /> : props.children}</CourseContext.Provider>;
};

export const useCourseContext = () => {
  return useContext(CourseContext);
};
